import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { getLocalizedBackendMessages } from './localizedMessagesUtils';
import { LooseObject } from './types';
import { UserRightAccessType, UserRightSection } from './types/dto-app';
import { User, UserRight } from './types/model-app';

export const DEFAULT_ADMIN_ROLE = 'admin';
const DEFAULT_FINANCE_ROLE = 'finance';
const DEFAULT_TECHNICAL_ROLE = 'technic';

export const PERMISSION_AGENCY_KEY_START = 'AGENCY_';

export type Capability =
  | 'payments:approve'
  | 'payments:execute'
  | 'payments:manage'
  | 'payments:view'
  | 'rentTracking:view'
  | 'supplierRegistry:manage'
  | 'treasury:view';

export interface CapabilityRequirement {
  section: UserRightSection;
  access: UserRightAccessType;
}

export const CAPABILITY_REQUIREMENTS: Record<Capability, CapabilityRequirement[]> = {
  'payments:approve': [{ section: UserRightSection.FINANCIAL_PAYMENTS_EXECUTION, access: UserRightAccessType.WRITE }],
  'payments:execute': [{ section: UserRightSection.FINANCIAL_PAYMENTS_EXECUTION, access: UserRightAccessType.WRITE }],
  'payments:manage': [{ section: UserRightSection.FINANCIAL_PAYMENTS, access: UserRightAccessType.WRITE }],
  'payments:view': [{ section: UserRightSection.FINANCIAL_PAYMENTS, access: UserRightAccessType.READ }],
  'rentTracking:view': [
    { section: UserRightSection.LEASES_DETAILS, access: UserRightAccessType.READ },
    { section: UserRightSection.FINANCIAL_TRANSACTIONS, access: UserRightAccessType.READ },
  ],
  'supplierRegistry:manage': [
    { section: UserRightSection.CONTACTS_DETAILS, access: UserRightAccessType.WRITE },
    { section: UserRightSection.FINANCIAL_PAYMENTS, access: UserRightAccessType.READ },
  ],
  'treasury:view': [{ section: UserRightSection.FINANCIAL_BALANCES, access: UserRightAccessType.READ }],
};

export const PAYMENT_WRITE_SECTIONS: UserRightSection[] = [
  UserRightSection.FINANCIAL_PAYMENTS,
  UserRightSection.FINANCIAL_PAYMENTS_EXECUTION,
];

export const MINIMAL_RIGHTS = [
  { section: UserRightSection.CONTACTS_DETAILS, access: [UserRightAccessType.READ] },
  { section: UserRightSection.BUILDINGS_UNITS_DETAILS, access: [UserRightAccessType.READ] },
];

export const DEFAULT_USERROLES = [
  {
    labelId: 'settings.rolesAndPermissions.defaultRoles.admin',
    key: DEFAULT_ADMIN_ROLE,
  },
  {
    labelId: 'settings.rolesAndPermissions.defaultRoles.finance',
    key: DEFAULT_FINANCE_ROLE,
  },
  {
    labelId: 'settings.rolesAndPermissions.defaultRoles.technic',
    key: DEFAULT_TECHNICAL_ROLE,
  },
];

export const PERMISSIONS_SECTIONS = [
  {
    labelId: 'settings.rolesAndPermissions.sections.contacts',
    keyStart: 'CONTACTS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.notifications',
    keyStart: 'NOTIFICATIONS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.communications',
    keyStart: 'COMMUNICATIONS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.templates',
    keyStart: 'TEMPLATES_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: false },
      { type: DEFAULT_TECHNICAL_ROLE, value: false },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.financial',
    keyStart: 'FINANCIAL_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: false },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },

  {
    labelId: 'settings.rolesAndPermissions.sections.leases',
    keyStart: 'LEASES_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.buildingsUnits',
    keyStart: 'BUILDINGS_UNITS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.tickets',
    keyStart: 'TICKETS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.advertisements',
    keyStart: 'ADVERTISEMENTS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_TECHNICAL_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.agencyRates',
    keyStart: PERMISSION_AGENCY_KEY_START,
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: true },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
  {
    labelId: 'settings.rolesAndPermissions.sections.settings',
    keyStart: 'SETTINGS_',
    defaults: [
      { type: DEFAULT_FINANCE_ROLE, value: false },
      { type: DEFAULT_TECHNICAL_ROLE, value: false },
      { type: DEFAULT_ADMIN_ROLE, value: true },
    ],
  },
];

export const ROLES_NO_DELETE: string[] = [
  'FINANCIAL_BALANCES',
  'FINANCIAL_PAYMENTS',
  'FINANCIAL_PAYMENTS_EXECUTION',
  'LEASES_SIGNATURE',
  'SETTINGS_MANAGEMENT',
  'SETTINGS_AUTOMATION',
  'TEMPLATES_CONTRACTS',
  'TEMPLATES_COMMUNICATIONS',
  'FINANCIAL_OCR',
];

export const ROLES_NO_WRITE: string[] = ['FINANCIAL_BALANCES'];

export const ROLES_NO_READ: string[] = [
  'LEASES_SIGNATURE',
  'SETTINGS_MANAGEMENT',
  'FINANCIAL_OCR',
  'FINANCIAL_PAYMENTS_EXECUTION',
];

export const getTablesForSection = (section: UserRightSection) => {
  switch (section) {
    case UserRightSection.NOTIFICATIONS_DETAILS:
      return ['Notification'];
    case UserRightSection.ADVERTISEMENTS_DETAILS:
      return ['Advertisement'];
    case UserRightSection.TICKETS_DETAILS:
      return ['Ticket', 'File'];
    case UserRightSection.CONTACTS_DETAILS:
      return ['Contact'];
    case UserRightSection.COMMUNICATIONS_DETAILS:
      return ['Communication'];
    case UserRightSection.TEMPLATES_CONTRACTS:
    case UserRightSection.TEMPLATES_COMMUNICATIONS:
      return ['Template'];
    case UserRightSection.FINANCIAL_MORTGAGES:
      return ['Mortgage'];
    case UserRightSection.FINANCIAL_TRANSACTIONS:
      return [
        'Transaction',
        'TransactionRule',
        'Posting',
        'Invoice',
        'BankAccount',
        'VariousOperation',
        'LeaseVariousOperation',
        'Charge',
        'ChargeStatement',
        'Statement',
      ];
    case UserRightSection.FINANCIAL_VALUATIONS_AND_COSTS:
      return ['Valuation', 'UnitEvent', 'BuildingEvent'];
    case UserRightSection.LEASES_CREATION:
    case UserRightSection.LEASES_DETAILS:
    case UserRightSection.LEASES_SIGNATURE:
      return [
        'Lease',
        'LeaseContact',
        'LeaseAdditionalClause',
        'UnitLease',
        'LeaseIndexation',
        'UnitEvent',
        'BankAccount',
        'File',
      ];
    case UserRightSection.BUILDINGS_UNITS_DETAILS:
      return ['Building', 'Unit', 'BuildingOwner', 'Address', 'UnitInventory', 'UnitOwner'];
    case UserRightSection.BUILDINGS_UNITS_TECHNICS:
    case UserRightSection.BUILDINGS_UNITS_UTILITY_PROVIDERS:
      return ['Technic', 'File'];
    case UserRightSection.AGENCY_RATE:
      return ['AgencyRate', 'AgencyRateOwner'];
    case UserRightSection.FINANCIAL_PAYMENTS:
    case UserRightSection.FINANCIAL_PAYMENTS_EXECUTION:
      return ['PaymentOrder'];
    case UserRightSection.FINANCIAL_BALANCES:
      return [];
    case UserRightSection.SETTINGS_AUTOMATION:
      return ['Setting', 'CommunicationSettingsProfile', 'TechnicLibrary', 'ScheduleRule', 'CustomField'];
    default:
      return [];
  }
};

export const isSectionWithAccessOn = (
  userRight: UserRight[],
  section: UserRightSection,
  access: UserRightAccessType
) => {
  return userRight.some((right) => right.section === section && right.access && right.access.includes(access));
};
export const isPaymentsWriteOn = (userRight: UserRight[]) =>
  PAYMENT_WRITE_SECTIONS.some((section) => isSectionWithAccessOn(userRight, section, UserRightAccessType.WRITE));
export const isSettingsOrganizationOn = (userRight: UserRight[]) => {
  return isSectionWithAccessOn(userRight, UserRightSection.SETTINGS_MANAGEMENT, UserRightAccessType.WRITE);
};
export const isLeaseSignatureWriteOn = (userRight: UserRight[]) => {
  return isSectionWithAccessOn(userRight, UserRightSection.LEASES_SIGNATURE, UserRightAccessType.WRITE);
};

export const isUserPartOfTopTeam = (user?: User | null) =>
  user?.teams?.some((userTeam) => isNil(userTeam.team?.parentId));

export interface DefaultRoleRight {
  section: UserRightSection;
  access: UserRightAccessType[];
}

export interface DefaultRoleData {
  key: string;
  name: string;
  userRights: DefaultRoleRight[];
}

const getRoleName = (language: string, roleKey: string): string => {
  const translation = getLocalizedBackendMessages(language);
  return (translation.settings.rolesAndPermissions.defaultRoles as LooseObject)[`${roleKey}`];
};

export const buildDefaultUserRoles = (language: string): DefaultRoleData[] =>
  DEFAULT_USERROLES.map((userRole) => {
    const userRights = Object.keys(UserRightSection).reduce((result, currentSection) => {
      const permissionSection = PERMISSIONS_SECTIONS.find((section) => currentSection.startsWith(section.keyStart));
      if (isNil(permissionSection)) {
        return result;
      }
      const sectionDefault = permissionSection.defaults.find((def) => def.type === userRole.key);
      if (isNil(sectionDefault) || !sectionDefault.value) {
        return result;
      }
      const access: UserRightAccessType[] = [];
      if (!ROLES_NO_READ.includes(currentSection)) {
        access.push(UserRightAccessType.READ);
      }
      if (!ROLES_NO_WRITE.includes(currentSection)) {
        access.push(UserRightAccessType.WRITE);
      }
      if (!ROLES_NO_DELETE.includes(currentSection)) {
        access.push(UserRightAccessType.DELETE);
      }
      result.push({ section: currentSection as UserRightSection, access });
      return result;
    }, [] as DefaultRoleRight[]);
    return {
      key: userRole.key,
      name: getRoleName(language, userRole.key),
      userRights: isEmpty(userRights) ? [] : userRights,
    };
  });
